import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { CcjRule, DEFAULT_CCJ_RULE, isCcjRule } from './ccjRules'

/** Same default the per-batch import panel uses — the Sentinel Reporting importPath. */
export const DEFAULT_DATA_PATH = 'srds/import/signatures'
const STORAGE_KEY = 'viewthru-import-all'
const POLL_MS = 1000
const RETRY_MS = 3000

/**
 * Eligibility checks the import is told to let clients through despite.
 * `client` holds client-level reason codes, `claim` agreement-level ones. A
 * client only passes when every check recorded against them is in the list.
 */
export interface ImportWaivers {
    client: string[]
    claim: string[]
}

export const EMPTY_WAIVERS: ImportWaivers = { client: [], claim: [] }

export const hasWaivers = (waivers: ImportWaivers | null | undefined) =>
    Boolean(waivers?.client?.length || waivers?.claim?.length)

const normaliseWaivers = (value: unknown): ImportWaivers => {
    const list = (input: unknown) =>
        Array.isArray(input)
            ? Array.from(new Set(input.map((item) => String(item ?? '').trim()).filter(Boolean)))
            : []
    const raw = (value || {}) as Partial<ImportWaivers>
    return { client: list(raw.client), claim: list(raw.claim) }
}

export interface ImportPreviewRow {
    clientId: number | string | null
    clientRef: string
    name: string
    email: string
    eligibleClaimCount: number
    value: number | null
    matchedBy?: string | null
    file?: { key: string } | null
    reasons?: string[]
    /** ISO date of birth, from the batch or the matched signature file. */
    dob?: string | null
    /** Checks this client only passed because they were waived. */
    waived?: { client: string[]; claim: string[]; claimCount: number }
    /** Already in Sentinel Reporting — from a previous run of this import or an earlier record. */
    alreadyImported?: boolean
}

/** A client the import never considered: an unwaived failure or a duplicate. */
export interface ImportExcludedRow {
    clientId: number | string | null
    name: string
    email: string
    dob?: string | null
    postcode?: string | null
    claimCount: number
    eligibleClaimCount: number
    value: number | null
    /** Reason codes — client-level failures, or `duplicate`. */
    reasons: string[]
    unwaivedReasons?: string[]
    waivedReasons?: string[]
    duplicateSources?: string[]
}

export interface ImportPreview {
    prefix?: string
    waivers?: ImportWaivers
    counts?: Record<string, number>
    importable?: ImportPreviewRow[]
    blocked?: ImportPreviewRow[]
    alreadyImportedClients?: ImportPreviewRow[]
    /** Not-eligible clients with at least one unwaived check. */
    notEligible?: ImportExcludedRow[]
    /** Duplicate-deducted clients held somewhere other than Sentinel Reporting. */
    duplicates?: ImportExcludedRow[]
    unmatchedFiles?: { key: string; name?: string; dob?: string | null; email: string; errors: string[] }[]
}

export interface ImportRun {
    id: string
    batchId?: string
    status: 'preparing' | 'running' | 'complete' | 'failed'
    total: number
    processed: number
    imported: number
    errors: number
    blocked: number
    currentClient?: string | null
    errorMessage?: string | null
}

export type PreviewState =
    | { status: 'running' }
    | { status: 'complete'; preview: ImportPreview }
    | { status: 'failed'; error: string }

export type ImportState =
    | { status: 'queued' }
    | { status: 'running'; run: ImportRun | null }
    | { status: 'complete'; run: ImportRun }
    | { status: 'failed'; run: ImportRun | null; error: string }

interface PersistedJob {
    dataPath: string
    subdirectory: string
    waivers?: ImportWaivers
    ccjRule?: CcjRule
    queue: string[]
    current: { batchId: string; runId: string } | null
}

/**
 * The request body shared by preview and import. Waivers are only sent when
 * set; the CCJ rule is always sent so the import's own analysis applies the
 * same tolerance the page analysed under.
 */
const importRequest = (
    batchId: string,
    dataPath: string,
    subdirectory: string,
    waivers: ImportWaivers,
    ccjRule: CcjRule
) => ({
    batchId,
    dataPath: dataPath.trim(),
    subdirectory: subdirectory.trim(),
    ccjRule,
    ...(hasWaivers(waivers) ? { waivers: { clientReasons: waivers.client, claimReasons: waivers.claim } } : {}),
})

const readJob = (): PersistedJob | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        return raw ? (JSON.parse(raw) as PersistedJob) : null
    } catch {
        return null
    }
}

const writeJob = (job: PersistedJob | null) => {
    if (job) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(job))
    else window.localStorage.removeItem(STORAGE_KEY)
}

const errorText = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.message || fallback

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Runs the per-batch import endpoints across many batches, one batch at a time
 * so a client that appears in two batches is imported once and then seen as
 * "already imported" by the next preview. Every rule — signature date
 * adjustment, transparent signature images, screening and funding gates — is
 * applied server-side by the same commit path the single-batch panel uses.
 */
export const useAllBatchesImport = ({ ccjRule = DEFAULT_CCJ_RULE }: { ccjRule?: CcjRule } = {}) => {
    const [dataPath, setDataPath] = useState(() => readJob()?.dataPath || DEFAULT_DATA_PATH)
    const [subdirectory, setSubdirectory] = useState(() => readJob()?.subdirectory || '')
    const [waivers, setWaiversState] = useState<ImportWaivers>(() => normaliseWaivers(readJob()?.waivers))
    const [previews, setPreviews] = useState<Record<string, PreviewState>>({})
    const [previewing, setPreviewing] = useState(false)
    const [imports, setImports] = useState<Record<string, ImportState>>({})
    const [importing, setImporting] = useState(false)
    const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null)
    const stopRef = useRef(false)
    const resumedRef = useRef(false)
    const lastRuleRef = useRef(ccjRule)

    const requestFor = useCallback(
        (batchId: string) => importRequest(batchId, dataPath, subdirectory, waivers, ccjRule),
        [ccjRule, dataPath, subdirectory, waivers]
    )

    const setPreview = (batchId: string, state: PreviewState) =>
        setPreviews((current) => ({ ...current, [batchId]: state }))
    const setImport = (batchId: string, state: ImportState) =>
        setImports((current) => ({ ...current, [batchId]: state }))

    /**
     * A preview taken under one CCJ rule does not describe the import under the
     * other, so switching rules discards every preview, like changing waivers.
     */
    useEffect(() => {
        if (lastRuleRef.current === ccjRule) return
        lastRuleRef.current = ccjRule
        setPreviews({})
    }, [ccjRule])

    /**
     * Changing which checks are waived changes who is importable, so every
     * existing preview is discarded: the import button only enables once the
     * batches have been previewed again under the new waivers.
     */
    const setWaivers = useCallback((next: ImportWaivers) => {
        setWaiversState(normaliseWaivers(next))
        setPreviews({})
    }, [])

    const toggleWaiver = useCallback(
        (kind: keyof ImportWaivers, reason: string) => {
            const list = waivers[kind]
            setWaivers({
                ...waivers,
                [kind]: list.includes(reason) ? list.filter((item) => item !== reason) : [...list, reason],
            })
        },
        [setWaivers, waivers]
    )

    const previewBatches = useCallback(
        async (batchIds: string[]) => {
            if (!batchIds.length || previewing || importing || !dataPath.trim()) return
            setPreviewing(true)
            setMessage(null)
            stopRef.current = false
            for (const batchId of batchIds) {
                if (stopRef.current) break
                setPreview(batchId, { status: 'running' })
                try {
                    const response = await api.post('viewthru-batch-import-preview', requestFor(batchId))
                    if (!response.data?.success) throw new Error(response.data?.message || 'Could not preview the import.')
                    setPreview(batchId, { status: 'complete', preview: response.data as ImportPreview })
                } catch (error: any) {
                    setPreview(batchId, { status: 'failed', error: errorText(error, 'Could not preview the import.') })
                }
            }
            setPreviewing(false)
        },
        [dataPath, importing, previewing, requestFor]
    )

    const pollRun = useCallback(async (batchId: string, runId: string): Promise<ImportRun> => {
        for (;;) {
            if (stopRef.current) throw new Error('Import polling stopped')
            try {
                const response = await api.post('viewthru-batch-import-status', { runId })
                if (!response.data?.success || !response.data?.run) {
                    throw new Error(response.data?.message || 'Could not read import progress.')
                }
                const run = response.data.run as ImportRun
                if (run.status === 'complete' || run.status === 'failed') return run
                setImport(batchId, { status: 'running', run })
                await sleep(POLL_MS)
            } catch (error: any) {
                if (stopRef.current) throw error
                // A transient dashboard request must not make a live background
                // import look as though it stopped; keep polling.
                setMessage({ tone: 'error', text: errorText(error, 'Progress is temporarily unavailable; the import is still running.') })
                await sleep(RETRY_MS)
            }
        }
    }, [])

    const runJob = useCallback(
        async (job: PersistedJob) => {
            setImporting(true)
            stopRef.current = false
            const jobWaivers = normaliseWaivers(job.waivers)
            const jobRule = isCcjRule(job.ccjRule) ? job.ccjRule : DEFAULT_CCJ_RULE
            const request = (batchId: string) => importRequest(batchId, job.dataPath, job.subdirectory, jobWaivers, jobRule)
            let queue = [...job.queue]
            let current = job.current
            for (const batchId of queue) setImport(batchId, { status: 'queued' })

            let totalImported = 0
            let totalErrors = 0
            let failedBatches = 0

            const settle = (batchId: string, run: ImportRun) => {
                totalImported += run.imported || 0
                totalErrors += run.errors || 0
                if (run.status === 'complete') setImport(batchId, { status: 'complete', run })
                else {
                    failedBatches += 1
                    setImport(batchId, { status: 'failed', run, error: run.errorMessage || 'The import stopped unexpectedly.' })
                }
            }

            try {
                if (current) {
                    const { batchId, runId } = current
                    setImport(batchId, { status: 'running', run: null })
                    settle(batchId, await pollRun(batchId, runId))
                    queue = queue.filter((id) => id !== batchId)
                    current = null
                    writeJob({ ...job, queue, current })
                }
                while (queue.length) {
                    if (stopRef.current) break
                    const batchId = queue[0]
                    setImport(batchId, { status: 'running', run: null })
                    let runId: string
                    try {
                        const response = await api.post('viewthru-batch-import-start', request(batchId))
                        const run = response.data?.run as ImportRun | undefined
                        if (!response.data?.success || !run?.id) {
                            throw new Error(response.data?.message || 'Could not start the import.')
                        }
                        runId = run.id
                        setImport(batchId, { status: 'running', run })
                    } catch (error: any) {
                        failedBatches += 1
                        setImport(batchId, { status: 'failed', run: null, error: errorText(error, 'Could not start the import.') })
                        queue = queue.slice(1)
                        writeJob({ ...job, queue, current: null })
                        continue
                    }
                    writeJob({ ...job, queue, current: { batchId, runId } })
                    settle(batchId, await pollRun(batchId, runId))
                    queue = queue.slice(1)
                    writeJob({ ...job, queue, current: null })
                }
                if (!stopRef.current) {
                    writeJob(null)
                    setMessage({
                        tone: totalErrors || failedBatches ? 'error' : 'success',
                        text:
                            `Imported ${totalImported} clients into Sentinel Reporting` +
                            (totalErrors ? `; ${totalErrors} clients failed` : '') +
                            (failedBatches ? `; ${failedBatches} batches did not finish` : '') +
                            '.',
                    })
                    setPreviews({})
                }
            } catch (error: any) {
                if (!stopRef.current) setMessage({ tone: 'error', text: errorText(error, 'The import stopped unexpectedly.') })
            } finally {
                setImporting(false)
            }
        },
        [pollRun]
    )

    const importBatches = useCallback(
        async (batchIds: string[]) => {
            if (!batchIds.length || importing || previewing || !dataPath.trim()) return
            setMessage(null)
            const job: PersistedJob = {
                dataPath: dataPath.trim(),
                subdirectory: subdirectory.trim(),
                waivers,
                ccjRule,
                queue: batchIds,
                current: null,
            }
            writeJob(job)
            await runJob(job)
        },
        [ccjRule, dataPath, importing, previewing, runJob, subdirectory, waivers]
    )

    /** Pick a job back up after a page reload; progress lives in MySQL so nothing is lost. */
    useEffect(() => {
        if (resumedRef.current) return
        resumedRef.current = true
        const job = readJob()
        if (job && (job.queue.length || job.current)) void runJob(job)
    }, [runJob])

    /** Stop driving the queue from this tab; a batch already started finishes server-side. */
    const stop = () => {
        stopRef.current = true
    }

    const clearResults = () => {
        setImports({})
        setPreviews({})
        setMessage(null)
    }

    return {
        dataPath,
        setDataPath,
        subdirectory,
        setSubdirectory,
        waivers,
        setWaivers,
        toggleWaiver,
        previews,
        previewing,
        previewBatches,
        imports,
        importing,
        importBatches,
        stop,
        message,
        clearResults,
    }
}
